import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import './TableComparison.css';
import { useAuth } from '../contexts/AuthContext';

interface ComparisonResult {
  sourceCount: number;
  targetCount: number;
  differences: RecordDifference[];
  identical: boolean;
  sourceEnv: string;
  targetEnv: string;
  schemaDifferences?: SchemaColumnDifference[];
  hasSchemaIssues?: boolean;
  tableExistsInSource?: boolean;
  tableExistsInTarget?: boolean;
}

interface RecordDifference {
  key: any;
  type: 'only-in-dev' | 'only-in-test' | 'only-in-prod' | 'different-values';
  sourceRecord: any;
  targetRecord: any;
  sourceEnv: string;
  targetEnv: string;
  fieldDifferences?: FieldDifference[];
}

interface FieldDifference {
  field: string;
  sourceValue: any;
  targetValue: any;
  isAuditField?: boolean;
}

interface SchemaColumnDifference {
  columnName: string;
  type: 'only-in-source' | 'only-in-target' | 'different-properties';
  sourceColumn?: any;
  targetColumn?: any;
  propertyDifferences?: string[];
}

interface TableComparisonProps {
  domainId: string;
}

interface TableInfo {
  schema: string;
  tableName: string;
  displayName: string;
  exists: boolean;
}

const TableComparison: React.FC<TableComparisonProps> = ({ domainId }) => {
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [sourceEnv, setSourceEnv] = useState<'DEV' | 'TEST' | 'PROD'>('DEV');
  const [targetEnv, setTargetEnv] = useState<'DEV' | 'TEST' | 'PROD'>('TEST');
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [syncing, setSyncing] = useState<boolean>(false);
  const [syncMessage, setSyncMessage] = useState<string>('');
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [loadingTables, setLoadingTables] = useState<boolean>(true);

  // Get permission checks from auth context
  const { canSyncBetweenEnvironments } = useAuth();
  const canSyncSourceToTarget = canSyncBetweenEnvironments(domainId, sourceEnv, targetEnv);
  const canSyncTargetToSource = canSyncBetweenEnvironments(domainId, targetEnv, sourceEnv);

  // Fetch tables for the selected domain
  useEffect(() => {
    const fetchDomainTables = async () => {
      try {
        setLoadingTables(true);
        setError('');
        const response = await apiService.getDomainTables(domainId);
        setTables(response.tables || []);

        // Reset selected table when domain changes
        setSelectedTable('');
        setComparisonResult(null);
      } catch (err: any) {
        console.error('Error fetching domain tables:', err);
        setError('Failed to load domain tables: ' + err.message);
        setTables([]);
      } finally {
        setLoadingTables(false);
      }
    };

    if (domainId) {
      fetchDomainTables();
    }
  }, [domainId]);

  const handleCompare = async () => {
    if (!selectedTable) {
      setError('Please select a table to compare');
      return;
    }

    const table = tables.find(t => `${t.schema}.${t.tableName}` === selectedTable);
    if (!table) return;

    setLoading(true);
    setError('');

    try {
      const result = await apiService.compareTable(table.schema, table.tableName, sourceEnv, targetEnv);
      setComparisonResult(result);
    } catch (err: any) {
      setError('Failed to compare table: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderDifferenceType = (type: string, diff: any) => {
    switch (type) {
      case 'different-values':
        return <span className="difference-type different-values">Different Values</span>;
      default:
        if (type.startsWith('only-in-')) {
          const env = type.replace('only-in-', '').toUpperCase();
          return <span className={`difference-type only-${env.toLowerCase()}`}>Only in {env}</span>;
        }
        return null;
    }
  };

  const renderValue = (value: any): string => {
    if (value === null || value === undefined) return 'NULL';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const handleSyncRecord = async (record: any, direction: 'source-to-target' | 'target-to-source') => {
    if (!selectedTable) return;

    const table = tables.find(t => `${t.schema}.${t.tableName}` === selectedTable);
    if (!table) return;

    const syncSourceEnv = direction === 'source-to-target' ? sourceEnv : targetEnv;
    const syncTargetEnv = direction === 'source-to-target' ? targetEnv : sourceEnv;

    // Prevent DEV to PROD direct sync
    if (syncSourceEnv === 'DEV' && syncTargetEnv === 'PROD') {
      setSyncMessage('❌ Direct sync from DEV to PROD is not allowed. Please sync DEV to TEST first, then TEST to PROD.');
      setTimeout(() => setSyncMessage(''), 5000);
      return;
    }

    setSyncing(true);
    setSyncMessage('');

    try {
      const result = await apiService.syncRecord(table.schema, table.tableName, record, syncSourceEnv, syncTargetEnv);

      if (result.success) {
        setSyncMessage(`✅ Record synced from ${syncSourceEnv} to ${syncTargetEnv}`);
        // Refresh comparison after successful sync
        setTimeout(() => {
          handleCompare();
        }, 1000);
      } else {
        setSyncMessage(`❌ Sync failed: ${result.error}`);
      }
    } catch (err: any) {
      setSyncMessage(`❌ Sync failed: ${err.message}`);
    } finally {
      setSyncing(false);
      // Clear message after 3 seconds
      setTimeout(() => setSyncMessage(''), 3000);
    }
  };

  const handleBulkSync = async (direction: 'source-to-target' | 'target-to-source', syncType: 'only-different' | 'all-differences') => {
    if (!selectedTable || !comparisonResult) return;

    const table = tables.find(t => `${t.schema}.${t.tableName}` === selectedTable);
    if (!table) return;

    const syncSourceEnv = direction === 'source-to-target' ? sourceEnv : targetEnv;
    const syncTargetEnv = direction === 'source-to-target' ? targetEnv : sourceEnv;

    // Prevent DEV to PROD direct sync
    if (syncSourceEnv === 'DEV' && syncTargetEnv === 'PROD') {
      setSyncMessage('❌ Direct bulk sync from DEV to PROD is not allowed. Please sync DEV to TEST first, then TEST to PROD.');
      setTimeout(() => setSyncMessage(''), 5000);
      return;
    }

    setSyncing(true);
    setSyncMessage('');

    try {
      // Get records to sync based on sync type
      let recordsToSync: any[] = [];
      let apiDirection: 'only-in-source' | 'different-values' | 'all' = 'all';

      if (syncType === 'only-different') {
        // Only sync records that have different values
        recordsToSync = comparisonResult.differences
          .filter(diff => diff.type === 'different-values')
          .map(diff => direction === 'source-to-target' ? diff.sourceRecord : diff.targetRecord)
          .filter(record => record !== null);
        apiDirection = 'different-values';
      } else {
        // Sync all differences (including only-in-source)
        recordsToSync = comparisonResult.differences
          .map(diff => {
            if (direction === 'source-to-target') {
              return (diff.type === `only-in-${sourceEnv.toLowerCase()}` || diff.type === 'different-values') ? diff.sourceRecord : null;
            } else {
              return (diff.type === `only-in-${targetEnv.toLowerCase()}` || diff.type === 'different-values') ? diff.targetRecord : null;
            }
          })
          .filter(record => record !== null);
        apiDirection = 'all';
      }

      if (recordsToSync.length === 0) {
        setSyncMessage('⚠️ No records to sync');
        setSyncing(false);
        return;
      }

      const result = await apiService.syncAllRecords(table.schema, table.tableName, recordsToSync, syncSourceEnv, syncTargetEnv, apiDirection);

      setSyncMessage(`✅ Bulk sync completed: ${result.successCount} succeeded, ${result.failureCount} failed`);

      // Refresh comparison after successful sync
      setTimeout(() => {
        handleCompare();
      }, 1000);
    } catch (err: any) {
      setSyncMessage(`❌ Bulk sync failed: ${err.message}`);
    } finally {
      setSyncing(false);
      // Clear message after 5 seconds for bulk operations
      setTimeout(() => setSyncMessage(''), 5000);
    }
  };

  return (
    <div className="table-comparison">
      <div className="comparison-header">
        <h2>Environment Comparison</h2>
        <p>Compare data between different environments to identify differences</p>
      </div>

      <div className="comparison-controls">
        <div className="table-selector">
          <label htmlFor="table-select">Select Table:</label>
          <select
            id="table-select"
            value={selectedTable}
            onChange={(e) => setSelectedTable(e.target.value)}
            disabled={loadingTables}
          >
            <option value="">-- Select a table --</option>
            {tables.map(table => (
              <option key={`${table.schema}.${table.tableName}`} value={`${table.schema}.${table.tableName}`}>
                {table.displayName}
              </option>
            ))}
          </select>
        </div>

        <div className="environment-selectors">
          <div className="env-selector">
            <label htmlFor="source-env">Source Environment:</label>
            <select
              id="source-env"
              value={sourceEnv}
              onChange={(e) => setSourceEnv(e.target.value as 'DEV' | 'TEST' | 'PROD')}
            >
              <option value="DEV">DEV</option>
              <option value="TEST">TEST</option>
              <option value="PROD">PROD</option>
            </select>
          </div>
          <div className="env-selector">
            <label htmlFor="target-env">Target Environment:</label>
            <select
              id="target-env"
              value={targetEnv}
              onChange={(e) => setTargetEnv(e.target.value as 'DEV' | 'TEST' | 'PROD')}
            >
              <option value="DEV">DEV</option>
              <option value="TEST">TEST</option>
              <option value="PROD">PROD</option>
            </select>
          </div>
        </div>

        <button
          className="compare-button"
          onClick={handleCompare}
          disabled={loading || !selectedTable || sourceEnv === targetEnv}
        >
          {loading ? 'Comparing...' : `Compare ${sourceEnv} vs ${targetEnv}`}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {syncMessage && (
        <div className={`sync-message ${syncMessage.includes('✅') ? 'success' : syncMessage.includes('⚠️') ? 'warning' : 'error'}`}>
          {syncMessage}
        </div>
      )}

      {comparisonResult && (
        <div className="comparison-results">
          <div className="results-header">
            <h3>Comparison Results</h3>
            <div className="summary">
              <div className="count-summary">
                <span className="source-count">{comparisonResult.sourceEnv}: {comparisonResult.sourceCount} records</span>
                <span className="target-count">{comparisonResult.targetEnv}: {comparisonResult.targetCount} records</span>
              </div>
              {comparisonResult.identical ? (
                <div className="identical-message">✓ Tables are identical</div>
              ) : (
                <div className="differences-message">
                  ⚠ {comparisonResult.differences.length} differences found
                </div>
              )}
            </div>
          </div>

          {/* Check if table doesn't exist in one or both environments */}
          {(comparisonResult.tableExistsInSource === false || comparisonResult.tableExistsInTarget === false) && (
            <div className="schema-warning table-missing-warning">
              <h4>⚠ Table Does Not Exist</h4>
              {comparisonResult.tableExistsInSource === false && comparisonResult.tableExistsInTarget === false ? (
                <p>This table does not exist in either {comparisonResult.sourceEnv} or {comparisonResult.targetEnv}.</p>
              ) : comparisonResult.tableExistsInSource === false ? (
                <p>This table does not exist in {comparisonResult.sourceEnv}. It only exists in {comparisonResult.targetEnv}.</p>
              ) : (
                <p>This table does not exist in {comparisonResult.targetEnv}. It only exists in {comparisonResult.sourceEnv}.</p>
              )}
            </div>
          )}

          {/* Show schema differences if table exists in both environments */}
          {comparisonResult.tableExistsInSource !== false && comparisonResult.tableExistsInTarget !== false &&
           comparisonResult.hasSchemaIssues && comparisonResult.schemaDifferences && comparisonResult.schemaDifferences.length > 0 && (
            <div className="schema-warning">
              <h4>⚠ Schema Differences Detected</h4>
              <p>The table schemas differ between {comparisonResult.sourceEnv} and {comparisonResult.targetEnv}. This may cause NULL values or missing data in comparisons.</p>
              <div className="schema-differences-list">
                {comparisonResult.schemaDifferences.map((diff, idx) => (
                  <div key={idx} className="schema-diff-item">
                    <strong>{diff.columnName}</strong>
                    {diff.type === 'only-in-source' && (
                      <span className="schema-badge only-source"> Only in {comparisonResult.sourceEnv}</span>
                    )}
                    {diff.type === 'only-in-target' && (
                      <span className="schema-badge only-target"> Only in {comparisonResult.targetEnv}</span>
                    )}
                    {diff.type === 'different-properties' && diff.propertyDifferences && (
                      <div className="property-diffs">
                        <span className="schema-badge different"> Different Properties:</span>
                        <ul>
                          {diff.propertyDifferences.map((propDiff, i) => (
                            <li key={i}>{propDiff}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!comparisonResult.identical && (
            <>
              <div className="bulk-sync-controls">
                <h4>Bulk Sync Options</h4>
                <div className="bulk-sync-buttons">
                  {canSyncSourceToTarget && (
                    <div className="sync-direction">
                      <span>{sourceEnv} → {targetEnv}:</span>
                      <button
                        onClick={() => handleBulkSync('source-to-target', 'only-different')}
                        disabled={syncing || (sourceEnv === 'DEV' && targetEnv === 'PROD')}
                        className="sync-button bulk-sync"
                        title={sourceEnv === 'DEV' && targetEnv === 'PROD' ? 'Direct sync from DEV to PROD is not allowed' : ''}
                      >
                        Sync Different Values →
                      </button>
                      <button
                        onClick={() => handleBulkSync('source-to-target', 'all-differences')}
                        disabled={syncing || (sourceEnv === 'DEV' && targetEnv === 'PROD')}
                        className="sync-button bulk-sync"
                        title={sourceEnv === 'DEV' && targetEnv === 'PROD' ? 'Direct sync from DEV to PROD is not allowed' : ''}
                      >
                        Sync All Differences →
                      </button>
                    </div>
                  )}
                  {canSyncTargetToSource && (
                    <div className="sync-direction">
                      <span>{targetEnv} → {sourceEnv}:</span>
                      <button
                        onClick={() => handleBulkSync('target-to-source', 'only-different')}
                        disabled={syncing || (targetEnv === 'DEV' && sourceEnv === 'PROD')}
                        className="sync-button bulk-sync"
                        title={targetEnv === 'DEV' && sourceEnv === 'PROD' ? 'Direct sync from DEV to PROD is not allowed' : ''}
                      >
                        ← Sync Different Values
                      </button>
                      <button
                        onClick={() => handleBulkSync('target-to-source', 'all-differences')}
                        disabled={syncing || (targetEnv === 'DEV' && sourceEnv === 'PROD')}
                        className="sync-button bulk-sync"
                        title={targetEnv === 'DEV' && sourceEnv === 'PROD' ? 'Direct sync from DEV to PROD is not allowed' : ''}
                      >
                        ← Sync All Differences
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="differences-list">
                {comparisonResult.differences.map((diff, index) => (
                  <div key={index} className="difference-item">
                    <div className="difference-header">
                      <span className="record-key">Record: {renderValue(diff.key)}</span>
                      {renderDifferenceType(diff.type, diff)}

                      {/* Sync buttons for individual records */}
                      <div className="record-sync-controls">
                        {canSyncSourceToTarget && ((diff.type === `only-in-${sourceEnv.toLowerCase()}` || diff.type === 'different-values') && diff.sourceRecord) && (
                          <button
                            onClick={() => handleSyncRecord(diff.sourceRecord, 'source-to-target')}
                            disabled={syncing || (sourceEnv === 'DEV' && targetEnv === 'PROD')}
                            className="sync-button individual"
                            title={sourceEnv === 'DEV' && targetEnv === 'PROD' ? 'Direct sync from DEV to PROD is not allowed' : `Sync ${sourceEnv} record to ${targetEnv}`}
                          >
                            {sourceEnv} → {targetEnv}
                          </button>
                        )}
                        {canSyncTargetToSource && ((diff.type === `only-in-${targetEnv.toLowerCase()}` || diff.type === 'different-values') && diff.targetRecord) && (
                          <button
                            onClick={() => handleSyncRecord(diff.targetRecord, 'target-to-source')}
                            disabled={syncing || (targetEnv === 'DEV' && sourceEnv === 'PROD')}
                            className="sync-button individual"
                            title={targetEnv === 'DEV' && sourceEnv === 'PROD' ? 'Direct sync from DEV to PROD is not allowed' : `Sync ${targetEnv} record to ${sourceEnv}`}
                          >
                            {targetEnv} → {sourceEnv}
                          </button>
                        )}
                      </div>
                    </div>

                  {diff.type === 'different-values' && diff.fieldDifferences && (
                    <div className="field-differences">
                      {/* Business Fields */}
                      <table className="field-diff-table">
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>{diff.sourceEnv || sourceEnv} Value</th>
                            <th>{diff.targetEnv || targetEnv} Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diff.fieldDifferences.filter(fd => !fd.isAuditField).map((fieldDiff, fieldIndex) => (
                            <tr key={fieldIndex}>
                              <td className="field-name">{fieldDiff.field}</td>
                              <td className="test-value">{renderValue(fieldDiff.sourceValue)}</td>
                              <td className="prod-value">{renderValue(fieldDiff.targetValue)}</td>
                            </tr>
                          ))}
                          {/* Updated Timestamp Row */}
                          {diff.fieldDifferences.filter(fd => fd.isAuditField).map((fieldDiff, index) => (
                            <tr key={`audit-${index}`}>
                              <td className="field-name" style={{ color: '#605E5C', fontStyle: 'italic', fontSize: '0.85rem' }}>
                                Last Updated
                              </td>
                              <td style={{ color: '#605E5C', fontStyle: 'italic', fontSize: '0.85rem' }}>
                                {renderValue(fieldDiff.sourceValue)}
                              </td>
                              <td style={{ color: '#605E5C', fontStyle: 'italic', fontSize: '0.85rem' }}>
                                {renderValue(fieldDiff.targetValue)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {diff.type === `only-in-${sourceEnv.toLowerCase()}` && diff.sourceRecord && (
                    <div className="record-details">
                      <h4>{sourceEnv} Record:</h4>
                      <table className="record-details-table">
                        <tbody>
                          {Object.entries(diff.sourceRecord).map(([key, value]) => (
                            <tr key={key}>
                              <td className="field-name">{key}</td>
                              <td className="field-value">{renderValue(value)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {diff.type === `only-in-${targetEnv.toLowerCase()}` && diff.targetRecord && (
                    <div className="record-details">
                      <h4>{targetEnv} Record:</h4>
                      <table className="record-details-table">
                        <tbody>
                          {Object.entries(diff.targetRecord).map(([key, value]) => (
                            <tr key={key}>
                              <td className="field-name">{key}</td>
                              <td className="field-value">{renderValue(value)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TableComparison;